<template>
  <div class="position-relative" :class="wrapperClass">
    <label v-if="label" class="font-14 input-label-spacing fw-500" :class="labelClass">
      <span>
        <span v-if="displayHTMLContent(label).type === 'html'" v-html="displayHTMLContent(label).content"></span>
        <template v-else>
          <i18n-t :keypath="label" tag="span" />
        </template>
      </span>
      <span class="text-danger" v-if="required === 'required'">*</span>
      <span v-if="pendingApproval" class="pending-approval-field-badge">
        <i class="mdi mdi-stamper"></i>
      </span>
    </label>
    <div :class="inputWrapperClass">
      <input
        v-bind="$attrs"
        v-on="inputListeners"
        :id="id || label"
        @input="updateValue"
        v-model="inputModelValue"
        @blur="handleBlur"
        @focus="handleFocus"
        @keypress="handleInput"
        class="base-input font-14 form-control"
        :class="itemClass"
        :placeholder="placeholder"
        ref="input"
        :type="type"
        :autocomplete="autocomplete"
        :maxlength="maxlength"
        :autofocus="autofocus"
        :tabindex="tabindex"
        :disabled="disabled"
        :readonly="readonly"
        v-prevent-number-scroll
      />
    </div>
    <slot></slot>
    <span v-if="spinner === true" class="spinner-border spinner-border-sm text-success spinner-input me-3" />
    <template v-if="customErrorMessage.length > 0 && invalid === true">
      <div class="invalid-feedback">
        <span> {{ customErrorMessage }}</span>
      </div>
    </template>
    <template v-else-if="errorMessages.length > 0 && invalid === true">
      <div class="invalid-feedback">
        <div v-for="(message, index) in errorMessages" :key="index">{{ message }}</div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

import { displayHTMLContent } from '@/utilities/helpers.js'

// #region - prop definitions
const props = defineProps({
  id: {
    type: String,
    required: false,
    default: ''
  },
  type: {
    type: String,
    required: false,
    default: ''
  },
  data: {
    type: Object,
    required: false,
    default: () => null
  },
  label: {
    type: String,
    required: false,
    default: ''
  },
  labelClass: {
    type: String,
    required: false,
    default: ''
  },
  modelValue: [String, Number],
  itemClass: {
    type: String,
    required: false,
    default: ''
  },
  required: {
    type: String,
    required: false,
    default: ''
  },
  spinner: {
    type: Boolean,
    required: false,
    default: false
  },
  format: {
    type: String,
    required: false,
    default: 'text'
  },
  placeholder: {
    type: String,
    required: false,
    default: ''
  },
  autocomplete: {
    type: String,
    required: false,
    default: ''
  },
  autofocus: {
    type: Boolean,
    required: false,
    default: false
  },
  maxlength: {
    required: false,
    type: Number
  },
  tabindex: {
    required: false,
    type: String
  },
  wrapperClass: {
    type: String,
    required: false,
    default: ''
  },
  inputWrapperClass: {
    type: String,
    required: false,
    default: ''
  },
  customErrorMessage: {
    type: String,
    required: false,
    default: ''
  },
  invalid: {
    type: Boolean,
    required: false,
    default: false
  },
  errorMessages: {
    type: Array,
    required: false,
    default: () => []
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false
  },
  readonly: {
    type: Boolean,
    required: false,
    default: false
  },
  pendingApproval: {
    type: Boolean,
    required: false,
    default: false
  }
})
// endregion

// #region - refs
const inputModelValue = ref(props.modelValue)
// endregion

// #region - emit definitions
const emit = defineEmits(['update:modelValue', 'blur', 'input', 'focus', 'validation'])
// endregion

// #region - Computed props
const inputListeners = computed(() => ({
  input: event => emit('update:modelValue', event.target.value)
}))
// endregion

// #region - References
const inputRef = ref(null)
// endregion

// #region - Method definitions
const handleBlur = event => {
  emit('blur', event.target.value)
}

const handleInput = event => {
  if (props.maxlength && event.target.value.length >= props.maxlength && props.format !== 'currency') {
    event.preventDefault()
  }
}

const updateValue = event => {
  let eventValue = event.target.value
  if (props.data) {
    eventValue = {
      ...props.data,
      value: eventValue
    }
  }
  inputModelValue.value = event.target.value
  emit('input', eventValue)
  emit('update:modelValue', eventValue)
}

const handleFocus = event => {
  emit('focus', event.target.value)
}

const focus = event => {
  inputRef.value?.focus()
}
// endregion

// #region - Expose part - focus method to the parent
defineExpose({
  focus
})
// endregion

// #region - Watchers
watch(
  () => props.modelValue,
  newModelValue => {
    inputModelValue.value = newModelValue
  }
)
// endregion
</script>

<style scoped>
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.ps-9 {
  padding-left: 9.45rem !important;
}

.input-default-size {
  height: 40px;
}

.custom-input-group-append {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-color: var(--primary-disabled) !important;
}

.column-content {
  padding: 9px 12px 24px 12px;
}

.base-input {
  &:hover {
    border-color: var(--input-border-color-hover);
  }
}
</style>
